import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getUserProfile, getUserAssessments } from '@/lib/db';
import type { Assessment, UserProfile } from '@/types/user';
import { DashboardPage } from '@/components/DashboardPage';
import { useToast } from '@/components/ui/use-toast';
import { BackButton } from '@/components/ui/back-button';

export default function Dashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      navigate('/barometre/login');
      return;
    }

    const loadData = async () => {
      try {
        if (currentUser.id) {
          const [profile, userAssessments] = await Promise.all([
            getUserProfile(currentUser.id),
            getUserAssessments(currentUser.id)
          ]);
          
          setUserProfile(profile);
          setAssessments(userAssessments);
        } else {
          throw new Error('User ID not found');
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger les données du tableau de bord.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser, navigate, toast]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const handleNavigate = (page: 'welcome' | 'diagnosis' | 'history' | 'results') => {
    switch (page) {
      case 'welcome':
        navigate('/barometre');
        break;
      case 'diagnosis':
        navigate('/barometre/assessment');
        break;
      case 'history':
        navigate('/barometre/history');
        break;
      case 'results':
        navigate('/barometre/results');
        break;
    }
  };

  return (
    <>
      <BackButton to="/barometre" />
      <DashboardPage
        onNavigate={handleNavigate}
        userName={userProfile?.displayName}
        userType={userProfile?.energyType}
        assessments={assessments}
        photoUrl={userProfile?.photoUrl}
      />
    </>
  );
}